import { z } from 'zod';

// Re-export types from OrganizationServiceApiClient.
export type {
  APIClientConfig,
  APIRequestOptions,
} from './OrganizationServiceApiClient';

// Form Date Schema
const FormDateSchema = z.object({
  startDate: z.string(),
  showDateOn: z.string(),
  registerDate: z.string(),
});

// Theme Colors Schema
const ThemeColorsSchema = z.object({
  primary: z.string(),
  secondary: z.string(),
  cta: z.string(),
  formBackground: z.string(),
  heroGradient: z.string(),
});

// Location Schema
const LocationSchema = z.object({
  address: z.string(),
  address2: z.string(),
  city: z.string(),
  stateId: z.number(),
  state: z.string(),
  zipcode: z.string(),
});

// Value Prop Schema
const ValuePropSchema = z.object({
  title: z.string(),
  description: z.string(),
  icon: z.string().optional(),
});

// Spotlight Block Schema
const SpotlightBlockSchema = z.object({
  title: z.string(),
  image: z.string(),
  text: z.string().optional(),
  list: z.array(
    z.object({
      text: z.string(),
    }),
  ),
});

// Spotlight Schema
const SpotlightSchema = z.object({
  voyagerSpotlightSchoolCardHeader: z.string(),
  voyagerSpotlightImage: z.string(),
  header: z.string(),
  blocks: z.array(SpotlightBlockSchema),
});

// Content Schema
const ContentSchema = z.object({
  description: z.string(),
  confirmation: z.object({
    meta: z.object({
      shortIntro: z.string().optional(),
      text: z.string().optional(),
      prepareCard: z
        .object({
          title: z.string(),
          text: z.string(),
        })
        .optional(),
    }),
  }),
  snippet: z.string(),
  summary: z.string(),
  tcpa: z.string(),
  valueProps: z.array(ValuePropSchema),
  spotlight: SpotlightSchema.optional(),
});

// Form Dates Schema
const FormDatesSchema = z.object({
  undergrad: z.array(FormDateSchema),
  grad: z.array(FormDateSchema),
});

// Program Flow Schema
const ProgramFlowSchema = z.object({
  providerProgramIdArray: z
    .union([z.array(z.string()), z.string()])
    .transform((val): string[] =>
      Array.isArray(val)
        ? val
        : val
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
    )
    .optional(),
  tcpa: z.string().optional(),
});

// Form Customizations Schema
const FormCustomizationsSchema = z.object({
  flow: z.string(),
  programFlow: z.array(ProgramFlowSchema),
  formName: z.string(),
  formDatesLabel: z.string(),
  showNextStartDate: z.boolean(),
  showActiveProspectScript: z.boolean().optional(),
});

// Partner Metadata Schema
const PartnerMetadataSchema = z.object({
  requiredProgramMetadata: z
    .array(
      z.object({
        name: z.string(),
        type: z.string(),
      }),
    )
    .nullable(),
  metadata: z.any(),
});

// Images Schema
const ImagesSchema = z.object({
  logo: z.string(),
  hero: z.string(),
});

// Organization Marketing Schema
export const OrganizationMarketingSchema = z.object({
  organizationId: z.number(),
  name: z.string(),
  mobiusIdToken: z.string(),
  tcpaSecondParagraph: z.string().optional(),
  slug: z.string(),
  homepage: z.string().optional(),
  tags: z.record(z.any()).optional(),
  themeColors: ThemeColorsSchema,
  citationText: z.string().optional(),
  sdbSchoolId: z.string(),
  priority: z.number(),
  location: LocationSchema,
  content: ContentSchema,
  formDates: FormDatesSchema,
  formCustomizations: FormCustomizationsSchema,
  partnerMetadata: PartnerMetadataSchema,
  images: ImagesSchema,
  additional: z.any(),
});

// Marketing Lookup Request Schema
export const MarketingLookupReqPayloadSchema = z.object({
  schoolIds: z.array(z.number()),
  isPreview: z.boolean().optional(),
  version: z.string().optional(),
});

// Marketing Lookup Response Schema
export const MarketingLookupResponseSchema = z.object({
  data: z.array(OrganizationMarketingSchema),
});

// Resolve External IDs Request Schema
export const ResolveExternalIdsReqPayloadSchema = z.object({
  edudirectIds: z.array(z.number()),
});

// Resolve External IDs Response Schema
export const ResolvedExternalIdSchema = z.object({
  edudirectId: z.number(),
  organizationId: z.string(),
});

export const ResolveExternalIdsResponseSchema = z.object({
  data: z.array(ResolvedExternalIdSchema),
});

// Type exports for use in TypeScript
export type OrganizationMarketing = z.infer<typeof OrganizationMarketingSchema>;
export type MarketingLookupReqPayload = z.infer<
  typeof MarketingLookupReqPayloadSchema
>;
export type MarketingLookupResponse = z.infer<
  typeof MarketingLookupResponseSchema
>;
export type ResolvedExternalId = z.infer<typeof ResolvedExternalIdSchema>;
export type ResolveExternalIdsReqPayload = z.infer<
  typeof ResolveExternalIdsReqPayloadSchema
>;
export type ResolveExternalIdsResponse = z.infer<
  typeof ResolveExternalIdsResponseSchema
>;
